(() => {
  'use strict';

  const CONFIG = {
    originAddress:
      '36 Cos Cob Avenue, Cos Cob, CT 06807, USA',

    maximumServiceDistanceMiles: 40,

    travelBands: [
      {
        minMiles: 0,
        maxMiles: 15,
        fee: 0
      },
      {
        minMiles: 15,
        maxMiles: 20,
        fee: 15
      },
      {
        minMiles: 20,
        maxMiles: 30,
        fee: 25
      },
      {
        minMiles: 30,
        maxMiles: 40,
        fee: 60
      }
    ]
  };

  const METERS_PER_MILE = 1609.344;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(
      'en-US',
      {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      }
    ).format(Number(amount) || 0);
  };

  const roundMiles = (miles) => {
    return Math.round(
      Number(miles) * 10
    ) / 10;
  };

  const getTravelFee = (distanceMiles) => {
    const distance =
      Number(distanceMiles);

    if (
      !Number.isFinite(distance) ||
      distance < 0
    ) {
      return null;
    }

    if (
      distance >
      CONFIG.maximumServiceDistanceMiles
    ) {
      return null;
    }

    if (distance <= 15) {
      return 0;
    }

    if (distance <= 20) {
      return 15;
    }

    if (distance <= 30) {
      return 25;
    }

    if (distance <= 40) {
      return 60;
    }

    return null;
  };

  const loadGoogleMaps = (apiKey) => {
    if (
      window.google?.maps?.places
    ) {
      return Promise.resolve();
    }

    if (
      window.DOLOBITES_GOOGLE_MAPS_PROMISE
    ) {
      return window
        .DOLOBITES_GOOGLE_MAPS_PROMISE;
    }

    window.DOLOBITES_GOOGLE_MAPS_PROMISE =
      new Promise(
        (resolve, reject) => {
          const callbackName =
            'initDolobitesGoogleMaps';

          window[callbackName] = () => {
            delete window[callbackName];
            resolve();
          };

          const script =
            document.createElement(
              'script'
            );

          script.src =
            'https://maps.googleapis.com/maps/api/js' +
            `?key=${encodeURIComponent(apiKey)}` +
            '&loading=async' +
            '&libraries=places,routes' +
            '&v=weekly' +
            `&callback=${callbackName}`;

          script.async = true;

          script.defer = true;

          script.onerror = () => {
            delete window[callbackName];

            reject(
              new Error(
                'Google Maps could not be loaded.'
              )
            );
          };

          document.head.appendChild(
            script
          );
        }
      );

    return window
      .DOLOBITES_GOOGLE_MAPS_PROMISE;
  };

  class DolobitesLocationV3 {
    constructor(booking) {
      this.booking = booking;

      this.input =
        booking.querySelector(
          '#db-event-location'
        );

      this.panel =
        this.input?.closest(
          '[data-step="3"]'
        );

      this.continueButton =
        this.panel?.querySelector(
          '[data-next-step]'
        );

      this.message = null;

      this.autocomplete = null;

      this.selectedPlace = null;

      this.isCalculating = false;

      this.inputChangedHandler =
        this.handleManualInput.bind(this);
    }

    init() {
      if (
        !this.input ||
        !this.panel ||
        !this.continueButton
      ) {
        return;
      }

      this.createMessage();

      this.resetLocation();

      this.input.addEventListener(
        'input',
        this.inputChangedHandler
      );

      const apiKey =
        window
          .DOLOBITES_GOOGLE_MAPS_API_KEY;

      if (!apiKey) {
        this.showError(
          'Google Maps configuration is missing.'
        );

        console.error(
          'window.DOLOBITES_GOOGLE_MAPS_API_KEY is missing.'
        );

        return;
      }

      loadGoogleMaps(apiKey)
        .then(() => {
          this.initializeAutocomplete();
        })
        .catch((error) => {
          console.error(error);

          this.showError(
            'Address search is temporarily unavailable. Please try again.'
          );
        });
    }

    createMessage() {
      let message =
        this.panel.querySelector(
          '#db-location-status'
        );

      if (!message) {
        message =
          document.createElement('div');

        message.id =
          'db-location-status';

        message.className =
          'db-booking__location-status';

        message.setAttribute(
          'aria-live',
          'polite'
        );

        this.input.insertAdjacentElement(
          'afterend',
          message
        );
      }

      this.message = message;
    }

    initializeAutocomplete() {
      if (
        !window.google?.maps?.places
          ?.Autocomplete
      ) {
        this.showError(
          'Google address search could not be initialized.'
        );

        return;
      }

      this.autocomplete =
        new google.maps.places
          .Autocomplete(
            this.input,
            {
              fields: [
                'formatted_address',
                'geometry',
                'name',
                'place_id'
              ],

              types: ['address'],

              componentRestrictions: {
                country: 'us'
              }
            }
          );

      this.autocomplete
        .addListener(
          'place_changed',
          () => {
            this.handlePlaceSelected();
          }
        );

      this.showNeutral(
        'Start typing and select an address from the suggestions.'
      );
    }

    handleManualInput() {
      /*
       * Ogni volta che l’utente modifica
       * manualmente l’indirizzo, la vecchia
       * selezione Google non è più valida.
       */
      this.selectedPlace = null;

      window
        .DOLOBITES_V3_LOCATION = null;

      window.setTimeout(() => {
        this.continueButton.disabled =
          true;
      }, 0);

      this.showNeutral(
        'Select a complete address from the Google suggestions.'
      );

      document.dispatchEvent(
        new CustomEvent(
          'dolobites:location-change',
          {
            detail: null
          }
        )
      );
    }

    async handlePlaceSelected() {
      const place =
        this.autocomplete.getPlace();

      if (
        !place ||
        !place.geometry ||
        !place.geometry.location
      ) {
        this.resetLocation();

        this.showError(
          'Please select an address from the Google suggestions.'
        );

        return;
      }

      this.selectedPlace = place;

      this.input.value =
        place.formatted_address ||
        place.name ||
        this.input.value;

      this.isCalculating = true;

      this.continueButton.disabled =
        true;

      this.showLoading(
        'Calculating travel distance…'
      );

      try {
        const distanceMiles =
          await this.calculateDistance(
            place
          );

        this.applyDistance(
          distanceMiles,
          place
        );
      } catch (error) {
        console.error(
          'Dolobites distance error:',
          error
        );

        this.resetLocation();

        this.showError(
          'We could not calculate the travel distance. Please select the address again.'
        );
      } finally {
        this.isCalculating = false;
      }
    }

    async calculateDistance(place) {
  const directionsService =
    new google.maps.DirectionsService();

  const destination =
    place.place_id
      ? {
          placeId: place.place_id
        }
      : place.formatted_address;

  const result =
    await directionsService.route({
      origin:
        CONFIG.originAddress,

      destination,

      travelMode:
        google.maps.TravelMode.DRIVING,

      provideRouteAlternatives:
        false
    });

  const route =
    result?.routes?.[0];

  const leg =
    route?.legs?.[0];

  const distanceMeters =
    Number(
      leg?.distance?.value
    );

  if (
    !Number.isFinite(
      distanceMeters
    ) ||
    distanceMeters <= 0
  ) {
    throw new Error(
      'No valid driving distance was returned.'
    );
  }

  return (
    distanceMeters /
    METERS_PER_MILE
  );
}

    applyDistance(
      rawDistanceMiles,
      place
    ) {
      const distanceMiles =
        roundMiles(
          rawDistanceMiles
        );

      const travelFee =
        getTravelFee(
          distanceMiles
        );

      const serviceable =
        distanceMiles <=
        CONFIG
          .maximumServiceDistanceMiles;

      const locationState = {
        address:
          place.formatted_address ||
          this.input.value,

        placeId:
          place.place_id || '',

        distanceMiles,

        travelFee,

        serviceable
      };

      window
        .DOLOBITES_V3_LOCATION =
        locationState;

      if (!serviceable) {
        this.continueButton.disabled =
          true;

        this.showError(
          `${distanceMiles} miles from Cos Cob. ` +
          'This address is outside our standard 40-mile service area. ' +
          'Please contact us for a custom quote.'
        );

        this.dispatchChange(
          locationState
        );

        return;
      }

      this.continueButton.disabled =
        false;

      if (travelFee === 0) {
        this.showSuccess(
          `${distanceMiles} miles from Cos Cob. Travel is included.`
        );
      } else {
        this.showSuccess(
          `${distanceMiles} miles from Cos Cob. ` +
          `Travel fee: ${formatCurrency(
            travelFee
          )}.`
        );
      }

      this.dispatchChange(
        locationState
      );

      /*
       * Avvisa anche gli altri moduli
       * che il valore dell’input è cambiato.
       */
      this.input.dispatchEvent(
        new Event(
          'change',
          {
            bubbles: true
          }
        )
      );
    }

    dispatchChange(detail) {
      document.dispatchEvent(
        new CustomEvent(
          'dolobites:location-change',
          {
            detail
          }
        )
      );
    }

    resetLocation() {
      window
        .DOLOBITES_V3_LOCATION = null;

      if (
        this.continueButton
      ) {
        this.continueButton.disabled =
          true;
      }
    }

    showNeutral(text) {
      this.setMessage(
        text,
        'is-neutral'
      );
    }

    showLoading(text) {
      this.setMessage(
        text,
        'is-loading'
      );
    }

    showSuccess(text) {
      this.setMessage(
        text,
        'is-success'
      );
    }

    showError(text) {
      this.setMessage(
        text,
        'is-error'
      );
    }

    setMessage(
      text,
      stateClass
    ) {
      if (!this.message) {
        return;
      }

      this.message.classList.remove(
        'is-neutral',
        'is-loading',
        'is-success',
        'is-error'
      );

      this.message.classList.add(
        stateClass
      );

      this.message.textContent =
        text;
    }
  }

  const initialize =
    (root = document) => {
      const bookings =
        Array.from(
          root.querySelectorAll(
            '.db-booking'
          )
        );

      bookings.forEach(
        (booking) => {
          if (
            booking.dataset
              .locationV3Initialized ===
            'true'
          ) {
            return;
          }

          booking.dataset
            .locationV3Initialized =
            'true';

          const instance =
            new DolobitesLocationV3(
              booking
            );

          booking
            .dolobitesLocationV3 =
            instance;

          instance.init();
        }
      );
    };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize()
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialize(
        event.target || document
      );
    }
  );
})();